import React, { useState, useEffect, useMemo } from 'react';
import { V } from '../../../utils/theme';
import { Card, Btn, Skeleton, SuccessToastCtrl } from '../../../components/ui';
import { Notice, Eyebrow } from '../../../components/kit';
import { fmtDay, fmtTime } from '../../../utils/org';
import { useApi, MiniField, MiniSelect } from './shared';
import { auditFilterBody, distinctActions, auditCsv, downloadText } from './helpers';

// ─── Audit tab ───
// audit_log {org_id, limit, cursor, action_filter?, actor_email?, target?, since?, until?}
// → { entries, next_cursor }. Export: audit_export {org_id, since, until, …filters} → { csv }
// saved through a Blob; on `no_org` / `forbidden` the same CSV is assembled client-side from
// audit_log pages (byte-compatible columns).
//
// The action filter travels as `action_filter` — `action` is the request's own action name
// and would clobber it. The API reads `action_filter` server-side and narrows the query, so
// a page is a page of *matches*: the rows that come back are rendered as they arrive, with
// no client-side re-filtering and no "on the pages loaded so far" caveat.
const PAGE = 50, EXPORT_PAGES = 50, EXPORT_PAGE = 100;
const EMPTY = { action: '', actor: '', target: '', since: '', until: '' };

export function AuditTab({ orgId, orgSlug }) {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [entries, setEntries] = useState(null);
  const [next, setNext] = useState(null);
  const [err, setErr] = useState('');
  const [more, setMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));

  const load = async (filters, cursor) => {
    if (cursor) setMore(true); else setEntries(null);
    setErr('');
    const r = await api('audit_log', { org_id: orgId, limit: PAGE, ...auditFilterBody(filters), ...(cursor ? { cursor } : {}) });
    if (r.ok) { setEntries(prev => (cursor ? [...(prev || []), ...(r.data.entries || [])] : (r.data.entries || []))); setNext(r.data.next_cursor || null); }
    else { setEntries(prev => prev || []); setErr(r.msg); }
    setMore(false);
  };
  useEffect(() => { load(applied); }, [orgId, applied]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = () => setApplied({ ...f });
  const clear = () => { setF(EMPTY); setApplied(EMPTY); };
  const actions = useMemo(() => distinctActions(entries, applied.action || f.action), [entries, applied.action, f.action]);

  const exportCsv = async () => {
    setExporting(true); setErr('');
    const body = auditFilterBody(applied);
    let csv = null, rows = 0, truncated = false;
    const r = await api('audit_export', { org_id: orgId, ...body });
    if (r.ok) { csv = r.data.csv; rows = r.data.rows ?? 0; truncated = !!r.data.truncated; }
    else if (['no_org', 'forbidden'].includes(r.err?.code)) {
      // Fallback: page the log with the same filters. The server applies them, so the
      // pages already hold only matching rows.
      const all = []; let cursor = null;
      for (let i = 0; i < EXPORT_PAGES; i++) {
        const p = await api('audit_log', { org_id: orgId, limit: EXPORT_PAGE, ...body, ...(cursor ? { cursor } : {}) });
        if (!p.ok) { setErr(p.msg); setExporting(false); return; }
        all.push(...(p.data.entries || []));
        cursor = p.data.next_cursor; if (!cursor) break;
        if (i === EXPORT_PAGES - 1) truncated = true;
      }
      csv = auditCsv(all); rows = all.length;
    } else { setErr(r.msg); setExporting(false); return; }
    const stamp = new Date().toISOString().slice(0, 10);
    if (downloadText(`ironlog-audit-${orgSlug || orgId}-${stamp}.csv`, csv)) SuccessToastCtrl.show(`Exported ${rows} row${rows === 1 ? '' : 's'}${truncated ? ' (truncated)' : ''}`);
    else setErr('Download is not available in this browser.');
    setExporting(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="org-audit">
      <Card>
        <Eyebrow style={{ marginBottom: 8 }}>Filters</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          <MiniSelect label="Action" value={f.action} onChange={set('action')} options={[['', 'All actions'], ...actions.map(a => [a, a])]} />
          <MiniField label="Actor" value={f.actor} onChange={set('actor')} type="email" placeholder="email" onEnter={apply} />
          <MiniField label="Target" value={f.target} onChange={set('target')} placeholder="email or code" onEnter={apply} />
          <MiniField label="From" value={f.since} onChange={set('since')} type="date" onEnter={apply} />
          <MiniField label="To" value={f.until} onChange={set('until')} type="date" onEnter={apply} />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          <Btn v="small" onClick={apply}>Apply</Btn>
          <Btn v="small" onClick={clear} s={{ background: V.hoverBg, color: V.text2, border: `1px solid ${V.cardBorder}` }}>Clear</Btn>
          <div style={{ flex: 1 }} />
          <Btn v="small" disabled={exporting} onClick={exportCsv}>{exporting ? 'Exporting…' : 'Export CSV'}</Btn>
        </div>
      </Card>
      {err && <Notice tone="error">{err}</Notice>}
      {entries === null ? <Card><Skeleton rows={6} /></Card> : (
        <Card>
          <Eyebrow style={{ marginBottom: 8 }}>Audit log · {entries.length} loaded{next ? '+' : ''}</Eyebrow>
          {entries.length === 0 ? <div style={{ fontSize: 12, color: V.text3 }}>Nothing matches these filters.</div> : entries.map((a, i) => (
            <div key={a.id || i} data-testid="audit-row" style={{ paddingTop: 6, paddingBottom: 6, borderTop: i ? `1px solid ${V.divider}` : 'none' }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: V.text, fontFamily: V.mono }}>{a.action}</span>
                <span style={{ fontSize: 10, color: V.text3, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.actor_email || a.actor}{a.target ? ` → ${a.target}` : ''}</span>
                <span style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, flexShrink: 0 }}>{fmtDay(a.created_at)} {fmtTime(a.created_at)}</span>
              </div>
              {a.detail && Object.keys(a.detail).length > 0 && <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, marginTop: 2, wordBreak: "break-all" }}>{JSON.stringify(a.detail).slice(0, 200)}</div>}
            </div>
          ))}
          {next && <Btn v="secondary" full disabled={more} onClick={() => load(applied, next)} s={{ marginTop: 10 }}>{more ? 'Loading…' : 'Load more'}</Btn>}
        </Card>
      )}
    </div>
  );
}
